<template>
  <AdaptiveSheet
    class="authLoginScreen"
    :opened="isOpen"
    :close-by-backdrop-click="false"
    :close-by-outside-click="false"
    tablet-fullscreen
    swipe-to-close
    :backdrop="false"
    full-screen
    style="min-height: 100%; --f7-popup-bg-color: #fff"
    @close="onPopupClose"
  >
    <f7-view>
      <f7-page>
        <div class="layout_content3">
          <div class="left_layout3"></div>
          <div class="main_layout3">
            <f7-login-screen-title>{{ $t("common.authenticateWithPIN") }}</f7-login-screen-title>
            <f7-block-header class="text-align-center">
              {{ $t("common.whereToFindSecretPIN") }}
              <br />
              <f7-link sheet-close popup-close class="link" href="/user/profile">{{ profile?.name || profile?.upn }}</f7-link>
            </f7-block-header>

            <div class="pin-display" :class="{ 'pin-display--locked': status === 'locked' }">
              {{ maskedValue }}
            </div>

            <div class="pin-feedback" :class="{ 'pin-feedback--error': showError, 'pin-feedback--lock': status === 'locked' }">
              <template v-if="status === 'locked'">
                {{ $t("common.pinLockedOut", { seconds: secondsLeft }) }}
              </template>
              <template v-else-if="justFailed">
                {{ $t("common.incorrectPIN", { leftTrials: attemptsLeft }) }}
              </template>
              <template v-else>&nbsp;</template>
            </div>

            <div class="pin-numpad" :class="{ 'pin-numpad--locked': status === 'locked' }">
              <f7-button
                v-for="digit in [1, 2, 3, 4, 5, 6, 7, 8, 9]"
                :key="digit"
                type="button"
                class="pin-key"
                :disabled="status === 'locked'"
                @click="append(digit)"
                fill
                round
              >
                {{ digit }}
              </f7-button>
              <span class="pin-key pin-key--empty"></span>
              <f7-button
                type="button"
                class="pin-key"
                :disabled="status === 'locked'"
                @click="append(0)"
                fill
                round
              >
                0
              </f7-button>
              <f7-button
                type="button"
                class="pin-key pin-key--back"
                :disabled="status === 'locked'"
                :aria-label="$t('common.delete')"
                @click="backspace()"
                fill
                round
              >
                <f7-icon f7="delete_left" :size="22" />
              </f7-button>
            </div>

            <div class="text-align-center padding-top">
              <f7-button large fill round raised @click="cancel">{{ $t("common.cancel") }}</f7-button>
            </div>
          </div>
          <div class="right_layout3"></div>
        </div>
      </f7-page>
    </f7-view>
  </AdaptiveSheet>
</template>

<style scoped>
.pin-display {
  min-height: 48px;
  font-size: 34px;
  letter-spacing: 0.35em;
  text-align: center;
  color: var(--f7-theme-color, #0009ff);
  font-weight: 600;
  padding: 18px 0 4px;
  -moz-user-select: none;
  -webkit-user-select: none;
  user-select: none;
}

.pin-display--locked {
  color: var(--f7-color-red, #ff3b30);
}

.pin-feedback {
  min-height: 22px;
  text-align: center;
  font-size: 13px;
  padding: 0 16px;
}

.pin-feedback--error {
  color: var(--f7-color-red, #ff3b30);
}

.pin-feedback--lock {
  color: var(--f7-color-orange, #ff9500);
  font-weight: 500;
}

.pin-numpad {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  padding: 16px 32px 0;
  max-width: 420px;
  margin: 0 auto;
}

.pin-numpad--locked {
  opacity: 0.6;
}

.pin-key {
  height: 64px;
  width: 64px;
  border-radius: 50%;
  font-size: 26px;
  font-weight: 500;
  color: var(--f7-text-color, #000);
  background: var(--f7-list-item-background-color, #fff);
  border: 1px solid var(--f7-page-master-border-color, rgba(0, 0, 0, 0.1));
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.pin-key:active:not(:disabled) {
  background: var(--f7-theme-color, #0009ff);
  color: #fff;
}

.pin-key:disabled {
  opacity: 0.5;
}

.pin-key--back {
  font-size: 22px;
}

.pin-key--empty {
  visibility: hidden;
}
</style>

<script setup>
import { computed } from "vue";
import { storeToRefs } from "pinia";
import AdaptiveSheet from "./common/AdaptiveSheet.vue";
import { useAuthStore } from "@/js/pinia-stores/auth.js";
import { useTransactAuthNumpad } from "@/js/composables/useTransactAuthNumpad.js";

const authStore = useAuthStore();
const { profile } = storeToRefs(authStore);

const { isOpen, value, attemptsLeft, secondsLeft, status, justFailed, cancel, append, backspace } =
  useTransactAuthNumpad();

const maskedValue = computed(() => {
  const filled = "*".repeat(value.value.length);
  const empty = "____".substring(0, 4 - value.value.length);
  return filled + empty;
});

const showError = computed(() => justFailed.value && status.value !== "locked");

function onPopupClose() {
  cancel();
}
</script>